/**
 * The tenant's sign-in and approved-programme access policy.
 *
 * @module components/admin/useAccessPolicy
 */

import { useEffect, useRef, useState } from "react";

import type { ProgrammePlatformClient } from "@/platform";
import type { TenantAccessPolicy } from "@/server/types";

export interface AccessPolicyState {
  /** Null until the first read answers, and if it fails. */
  policy: TenantAccessPolicy | null;
  setPolicy: (policy: TenantAccessPolicy) => void;
  /** True until the first read answers, one way or the other. */
  loading: boolean;
  /** True while a write is in flight, so a second press is refused. */
  saving: boolean;
  /** What the last read or save said. Empty until one of them speaks. */
  feedback: string;
  /** Whether that last word was a failure, which the screen reports as one. */
  failed: boolean;
  save: () => Promise<void>;
  /** Read the policy again, after a read that failed. */
  reload: () => void;
}

/**
 * @param client - The platform client to read and write through.
 * @param tenantId - The tenant whose policy this is.
 * @returns The policy, an editor for it, and the save.
 *
 * @example
 * ```tsx
 * const { policy, setPolicy, loading, feedback, save } = useAccessPolicy(client, tenant.id);
 * ```
 */
export function useAccessPolicy(
  client: ProgrammePlatformClient,
  tenantId: string,
): AccessPolicyState {
  // Keyed to tenantId: on tenant change, state for a different tenant is treated
  // as loading and unpopulated until the new read resolves.
  const [loaded, setLoaded] = useState<{
    tenantId: string;
    policy: TenantAccessPolicy | null;
    loading: boolean;
    feedback: string;
    failed: boolean;
  }>({
    tenantId,
    policy: null,
    loading: true,
    feedback: "",
    failed: false,
  });
  const [attempt, setAttempt] = useState(0);

  const [edits, setEdits] = useState<{ tenantId: string; policy: TenantAccessPolicy | null }>({
    tenantId,
    policy: null,
  });

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    client
      .getTenantAccessPolicy()
      .then((next) => {
        if (active) {
          setLoaded({ tenantId, policy: next, loading: false, feedback: "", failed: false });
        }
      })
      .catch((cause: unknown) => {
        if (active) {
          setLoaded({
            tenantId,
            policy: null,
            loading: false,
            feedback: cause instanceof Error ? cause.message : "Unable to load the access policy",
            failed: true,
          });
        }
      });
    return () => {
      active = false;
    };
  }, [attempt, client, tenantId]);

  const isCurrentTenant = loaded.tenantId === tenantId;
  let policy: TenantAccessPolicy | null = null;
  if (edits.tenantId === tenantId && edits.policy !== null) {
    policy = edits.policy;
  } else if (isCurrentTenant) {
    policy = loaded.policy;
  }
  const loading = isCurrentTenant ? loaded.loading : true;
  const feedback = isCurrentTenant ? loaded.feedback : "";
  const failed = isCurrentTenant && loaded.failed;

  const setPolicy = (next: TenantAccessPolicy | null): void => {
    setEdits({ tenantId, policy: next });
  };

  // A ref beside the state: two presses in one tick both read the same
  // `saving`, so the state alone refuses the second only after a render.
  const inFlight = useRef(false);

  const save = async (): Promise<void> => {
    if (!policy || loading || !isCurrentTenant || inFlight.current) {
      return;
    }
    inFlight.current = true;
    setSaving(true);
    setLoaded((current) => ({ ...current, feedback: "", failed: false }));
    try {
      const updated = await client.updateTenantAccessPolicy(policy);
      setEdits({ tenantId, policy: updated });
      setLoaded({
        tenantId,
        policy: updated,
        loading: false,
        feedback: "Access policy saved.",
        failed: false,
      });
    } catch (cause) {
      setLoaded((current) => ({
        ...current,
        feedback: cause instanceof Error ? cause.message : "Unable to save the access policy",
        failed: true,
      }));
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  };

  const reload = (): void => {
    setLoaded({ tenantId, policy: null, loading: true, feedback: "", failed: false });
    setAttempt((current) => current + 1);
  };

  return { policy, setPolicy, loading, saving, feedback, failed, save, reload };
}
